import { useId } from 'react'

/**
 * Sittings and the children behind them, day by day — one drawing, three tiers.
 *
 * 🔴 THIS IS `ClassActivityChart`, MOVED UP A LEVEL BECAUSE A SECOND TIER
 * NEEDED IT. It was written for the teacher's D4 and its reasoning is unchanged
 * and reproduced below. What changed is that the school dashboard now asks the
 * same question of a whole school, and the honest options were "draw the same
 * chart from one component" or "write a second one that can disagree with the
 * first about what a quiet week looks like". `architecture.md` §12 is explicit
 * about which of those is allowed.
 *
 * 🔴 IT NO LONGER IMPORTS `ActivityPoint` FROM `api/teaching`. A shared drawing
 * that reaches into one tier's API module is a dependency pointing the wrong
 * way — the school's payload has the identical shape and its own type. The
 * structural type below is what both satisfy, and neither tier has to know
 * about the other.
 *
 * 🔴 THE OPERATOR TIER'S CHART, SCOPED DOWN. `GrowthChart` is the reference: an
 * inline SVG polyline over real points, no charting dependency, a labelled floor
 * rather than a hidden one. What is deliberately NOT copied is its cumulative
 * reading — that file's own note explains why a platform curve must be
 * cumulative (*"mostly zeroes with an occasional spike"*), and a classroom is
 * the opposite case. A running total of sittings only goes up, so a class that
 * stopped three weeks ago would be drawn as a comfortable flat line.
 *
 * 🔴 TWO SERIES BECAUSE THEY ANSWER DIFFERENT QUESTIONS. `sessions` is how much
 * play happened; `children` is how many of them it was spread across. One child
 * playing five times and five children playing once are the same number of
 * sittings and very different mornings — and the gap between the two lines IS
 * the finding a teacher acts on.
 *
 * ⚠️ IT DRAWS EVERY DAY THE SERVER SENDS, ZEROES INCLUDED, and must never
 * filter. Dropping empty days closes the gaps, so a class that plays once a week
 * is drawn identically to one that plays daily.
 *
 * ⚠️ NO SMOOTHING. Straight segments between real points; a curve would invent
 * values on days nobody played, which on this chart is the one lie that matters.
 *
 * ⚠️ NOT AN `svg` WITH A FIXED WIDTH — `viewBox` plus `w-full` lets one drawing
 * serve a phone and a 1366px laptop, which is the responsive rule this tier's
 * shell already assumes.
 */

/**
 * One day of play.
 *
 * ⚠️ STRUCTURAL ON PURPOSE. `ActivityPoint` in `api/teaching` and the school's
 * own row both satisfy this without either importing the other.
 */
export interface ActivityLinePoint {
  /** `YYYY-MM-DD`, UTC. */
  day: string
  /** Sittings that started on this day. */
  sessions: number
  /** Distinct children who had at least one sitting — NOT the same as `sessions`. */
  children: number
}

export interface ActivityLineChartProps {
  points: readonly ActivityLinePoint[]
  /** Localised words — this component emits none of its own (`GamesPanel`'s rule). */
  copy: {
    sessions: string
    children: string
    /** Drawn when every day in the window is zero. */
    nothingYet: string
  }
  /**
   * Drawing height in viewBox units.
   *
   * 🔴 THE SCHOOL'S HERO CHART IS TALLER THAN THE TEACHER'S PANEL, and that is
   * the only difference between the two mounts. A head teacher reads this as
   * the answer to "is the product being used at all" and it is the largest
   * thing on the page; a class teacher reads it as one panel among four. Same
   * geometry, same maths, more room.
   */
  height?: number
}

const W = 640
const DEFAULT_H = 180
const PAD = { top: 10, right: 6, bottom: 22, left: 6 }

/* Two of §3's fixed tints, chosen for the one pair that stays distinguishable
   under the common colour deficiencies — the legend carries the name as well,
   so colour is never the only carrier (§12). */
const SESSIONS_STROKE = 'var(--color-indigo)'
const CHILDREN_STROKE = 'var(--color-leaf)'

/**
 * The drawing ceiling.
 *
 * 🔴 THE FLOOR IS ALWAYS ZERO HERE, WHICH IS THE OPPOSITE OF `GrowthChart`'s
 * RULE AND RIGHT FOR THE SAME REASON. That chart refuses a zero baseline because
 * a cumulative curve moves by a few per cent of its own height. These counts are
 * small integers that genuinely reach zero, and "no sittings" is the reading the
 * teacher came for — lifting the floor would draw a quiet week as a busy one.
 *
 * ⚠️ A CLASS WITH NO PLAY AT ALL GETS A CEILING OF 1, so the flat line sits on
 * the floor instead of dividing by zero.
 */
export function ceiling(points: readonly ActivityLinePoint[]): number {
  const values = points.flatMap((p) => [p.sessions, p.children])
  return Math.max(1, ...values)
}

/** One series as an SVG polyline. */
export function pathFor(
  points: readonly ActivityLinePoint[],
  key: 'sessions' | 'children',
  max: number,
  height: number = DEFAULT_H,
): string {
  if (points.length === 0) return ''
  const innerW = W - PAD.left - PAD.right
  const innerH = height - PAD.top - PAD.bottom
  /* ⚠️ A SINGLE POINT IS DRAWN DOWN THE MIDDLE rather than at x=0, where it
     would read as the start of a line that fell off the chart. */
  const step = points.length > 1 ? innerW / (points.length - 1) : 0
  const originX = points.length > 1 ? PAD.left : PAD.left + innerW / 2

  return points
    .map((p, i) => {
      const x = originX + i * step
      const y = PAD.top + innerH - (p[key] / max) * innerH
      return `${i === 0 ? 'M' : 'L'}${String(Math.round(x * 10) / 10)},${String(
        Math.round(y * 10) / 10,
      )}`
    })
    .join(' ')
}

/** `2026-08-18` → `18 Aug`. The axis needs the day and the month, never the year. */
export function axisLabel(day: string): string {
  const at = new Date(`${day}T00:00:00.000Z`)
  if (Number.isNaN(at.getTime())) return day
  return at.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  })
}

/**
 * Which days get a printed label.
 *
 * ⚠️ FOUR OR FIVE, NEVER ONE PER DAY. Thirty dates along 640px overlap into a
 * grey smear; the ends always carry one so the reader knows what window they are
 * looking at without reading the panel's title.
 */
export function ticks(points: readonly ActivityLinePoint[]): number[] {
  if (points.length <= 5) return points.map((_, i) => i)
  const gaps = 4
  return Array.from({ length: gaps + 1 }, (_, i) =>
    Math.round((i * (points.length - 1)) / gaps),
  )
}

export function ActivityLineChart({
  points,
  copy,
  height = DEFAULT_H,
}: ActivityLineChartProps) {
  const titleId = useId()
  const max = ceiling(points)
  const silent = points.every((p) => p.sessions === 0 && p.children === 0)

  return (
    <div className="flex flex-col gap-3">
      {/*
        🔴 THE LEGEND IS ABOVE THE DRAWING, NOT UNDER IT. A reader who meets the
        lines first has to hold two unnamed colours in their head until they
        reach the key — §12's rule that colour is never the only carrier is only
        satisfied if the name arrives first.
      */}
      <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
        {[
          { label: copy.sessions, tint: SESSIONS_STROKE },
          { label: copy.children, tint: CHILDREN_STROKE },
        ].map((seri) => (
          <span
            key={seri.label}
            className="flex items-center gap-1.5 font-dash text-dash-xs font-bold text-muted"
          >
            <span
              aria-hidden="true"
              className="inline-block h-2.5 w-2.5 rounded-full"
              style={{ background: seri.tint }}
            />
            {seri.label}
          </span>
        ))}
      </span>

      <div className="relative">
        <svg
          viewBox={`0 0 ${String(W)} ${String(height)}`}
          className="w-full"
          role="img"
          aria-labelledby={titleId}
          preserveAspectRatio="none"
        >
          {/*
            ⚠️ THE ACCESSIBLE NAME IS THE FINDING, NOT "a chart". A reader who
            cannot see it needs the same sentence the picture makes.
          */}
          <title id={titleId}>
            {`${copy.sessions} and ${copy.children}, ${String(points.length)} days`}
          </title>

          {/*
            🔴 THE HORIZONTAL RULES ARE NEW, AND THEY ARE THE ONE THING THIS
            DRAWING WAS MISSING. Two lines over a bare floor let a reader see
            SHAPE and nothing else — whether Tuesday was twice Monday or a
            tenth more is unreadable without something to measure against.
            Three rules at quarter heights give the eye a ruler.

            ⚠️ THEY CARRY NO NUMBERS. A labelled y-axis on a 640×180 box that
            stretches to any column width would either overlap or lie about its
            own scale, and the panel's title already names what is counted. The
            rules are a grid, not a claim.
          */}
          {[0.25, 0.5, 0.75].map((f) => (
            <line
              key={f}
              x1={PAD.left}
              x2={W - PAD.right}
              y1={PAD.top + (height - PAD.top - PAD.bottom) * f}
              y2={PAD.top + (height - PAD.top - PAD.bottom) * f}
              stroke="var(--color-line)"
              strokeWidth={1}
              strokeDasharray="3 5"
              opacity={0.7}
              vectorEffect="non-scaling-stroke"
            />
          ))}

          {/* The floor, drawn so a flat line at zero is visibly ON something. */}
          <line
            x1={PAD.left}
            x2={W - PAD.right}
            y1={height - PAD.bottom}
            y2={height - PAD.bottom}
            stroke="var(--color-line-strong)"
            strokeWidth={1}
            vectorEffect="non-scaling-stroke"
          />

          <path
            d={pathFor(points, 'sessions', max, height)}
            fill="none"
            stroke={SESSIONS_STROKE}
            strokeWidth={2.5}
            strokeLinejoin="round"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
          <path
            d={pathFor(points, 'children', max, height)}
            fill="none"
            stroke={CHILDREN_STROKE}
            strokeWidth={2.5}
            strokeLinejoin="round"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
        </svg>

        {/*
          ⚠️ THE AXIS IS HTML BESIDE THE SVG, NOT `<text>` INSIDE IT. The drawing
          uses `preserveAspectRatio="none"` so it can stretch to any column
          width, which would stretch glyphs with it — text laid out by the
          browser stays the size the rest of the tier is set in.
        */}
        <div className="mt-1 flex justify-between font-dash text-dash-xs text-muted">
          {ticks(points).map((i) => (
            <span key={points[i]?.day ?? i} className="tabular">
              {points[i] ? axisLabel(points[i].day) : ''}
            </span>
          ))}
        </div>

        {/*
          🔴 A CLASS THAT HAS NOT PLAYED GETS A SENTENCE ON TOP OF ITS OWN FLAT
          LINE, not an empty box. §"Every data surface ships four states": the
          empty state is *"a sentence plus one action — never blank white
          space"*, and the flat line is the honest drawing underneath it.
        */}
        {silent && (
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <span className="rounded-pill bg-canvas px-3 py-1 font-dash text-dash-xs font-bold text-muted">
              {copy.nothingYet}
            </span>
          </span>
        )}
      </div>
    </div>
  )
}
